// src/admin/AdminAdPaymentsList.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stack,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  TablePagination,
  Switch,
  CircularProgress,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import api from "../utils/api";
import { API } from "../config/apiConfig";
import AdminPageWrapper from './components/AdminPageWrapper';
import ProfessionalTable from '../components/ProfessionalTable';

// Endpoints (flexible fallbacks to match your apiConfig)
const PAYMENTS_ENDPOINT =
  API.adminAdPayments || "/api/admin/adpayments";
const LOOKUPS_ENDPOINT =
  API.adminAdPaymentsLookups ||
  (API.adminAdPayments ? `${API.adminAdPayments}/lookups` : "/api/admin/adpayments/lookups");

// --- Helpers ---
const pad = (n) => String(n).padStart(2, "0");
const toYMD = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};
const firstOfThisMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};
const lastOfThisMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0);
};
const fmtMoney = (v, dp = 2) => {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n.toFixed(dp) : (0).toFixed(dp);
};
const csvSafe = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return s.includes(",") || s.includes('"') || s.includes("\n")
    ? `"${s.replace(/"/g, '""')}"`
    : s;
};
const keyOf = (r) =>
  r.PaymentID ?? r.Id ?? `${r.ProviderId}-${r.AppId}-${r.PeriodStart}-${r.PeriodEnd}`;

// --- Component ---
export default function AdminAdPaymentsList() {
  // filters
  const [start, setStart] = useState(firstOfThisMonth());
  const [end, setEnd] = useState(lastOfThisMonth());
  const [searchTerm, setSearchTerm] = useState("");
  const [providerId, setProviderId] = useState("");
  const [appId, setAppId] = useState("");
  const [onlyProcessed, setOnlyProcessed] = useState(""); // "", "1", "0"

  // lookups
  const [providers, setProviders] = useState([]); // [{ ProviderId, ProviderName }]
  const [apps, setApps] = useState([]);           // [{ AppId, AppName }]

  // table data
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // 0-based in MUI
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);

  // ui state
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const periodLabel = useMemo(
    () => `${toYMD(start)} → ${toYMD(end)}`,
    [start, end]
  );

  // lookups + initial list
  useEffect(() => {
    fetchLookups();
    fetchPayments({ resetPage: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- API calls ---
  async function fetchLookups() {
    try {
      const { data } = await api.get(LOOKUPS_ENDPOINT);
      setProviders(Array.isArray(data?.providers) ? data.providers : []);
      setApps(Array.isArray(data?.apps) ? data.apps : []);
    } catch (err) {
      console.warn("Lookups not available, continuing without:", err?.message);
    }
  }

  async function fetchPayments({ resetPage = false } = {}) {
    setLoading(true);
    try {
      const params = {
        periodStart: toYMD(start),
        periodEnd: toYMD(end),
        searchTerm: searchTerm || undefined,
        providerId: providerId || undefined,
        appId: appId || undefined,
        isProcessed:
          onlyProcessed === "" ? undefined : onlyProcessed === "1",
        page: resetPage ? 1 : page + 1, // 1-based for backend
        pageSize: rowsPerPage,
        sortBy: "CreatedAt",
        sortDir: "DESC",
      };
      const { data } = await api.get(PAYMENTS_ENDPOINT, { params });

      const items =
        data?.items ??
        data?.payments ??
        (Array.isArray(data) ? data : []);
      const count = data?.total ?? data?.count ?? items.length;

      setRows(items || []);
      setTotal(Number(count) || 0);
      if (resetPage) setPage(0);
      setSuccessMsg("Payments loaded");
    } catch (err) {
      console.error("❌ Fetch payments failed:", err);
      setErrorMsg(err?.response?.data?.error || "Failed to load payments");
    } finally {
      setLoading(false);
    }
  }

  async function toggleProcessed(row, newVal) {
    const prev = [...rows];
    setRows((rs) =>
      rs.map((r) => (keyOf(r) === keyOf(row) ? { ...r, IsProcessed: newVal } : r))
    );
    try {
      const url = `${PAYMENTS_ENDPOINT}/${row.PaymentID ?? row.Id ?? ""}/processed`;
      await api.patch(url, { isProcessed: newVal });
      setSuccessMsg(`Marked as ${newVal ? "processed" : "unprocessed"}`);
    } catch (err) {
      setRows(prev);
      setErrorMsg(err?.response?.data?.error || "Failed to update status");
    }
  }

  // --- Handlers ---
  const handleThisMonth = async () => {
    setStart(firstOfThisMonth());
    setEnd(lastOfThisMonth());
    await fetchPayments({ resetPage: true });
  };

  const handleStartChange = (e) => {
    const d = new Date(e.target.value);
    if (!Number.isNaN(d.getTime())) {
      setStart(d);
      if (end < d) setEnd(d);
    }
  };
  const handleEndChange = (e) => {
    const d = new Date(e.target.value);
    if (!Number.isNaN(d.getTime()) && d >= start) {
      setEnd(d);
    }
  };

  const handleChangePage = async (_e, newPage) => {
    setPage(newPage);
    await fetchPayments();
  };

  const handleChangeRowsPerPage = async (e) => {
    const v = parseInt(e.target.value, 10);
    setRowsPerPage(v);
    setPage(0);
    await fetchPayments({ resetPage: true });
  };

  const handleRefresh = async () => {
    await fetchPayments({ resetPage: true });
  };

  const exportCsv = () => {
    const headers = [
      "PaymentID",
      "ProviderId",
      "ProviderName",
      "AppId",
      "AppName",
      "PeriodStart",
      "PeriodEnd",
      "AmountPaid",
      "Currency",
      "FxRateToUSD",
      "AmountPaidUSD",
      "IsProcessed",
      "Notes",
      "CreatedAt",
    ];
    const csv = [
      headers.join(","),
      ...rows.map((r) =>
        [
          r.PaymentID ?? r.Id ?? "",
          r.ProviderId ?? r.ProviderID ?? "",
          csvSafe(r.ProviderName),
          r.AppId ?? r.AppID ?? "",
          csvSafe(r.AppName),
          r.PeriodStart ? toYMD(r.PeriodStart) : "",
          r.PeriodEnd ? toYMD(r.PeriodEnd) : "",
          r.AmountPaid ?? "",
          r.Currency ?? "",
          r.FxRateToUSD ?? "",
          r.AmountPaidUSD ?? "",
          r.IsProcessed ? "1" : "0",
          csvSafe(r.Notes),
          r.CreatedAt ? new Date(r.CreatedAt).toISOString() : "",
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ad-payments_${toYMD(start)}_${toYMD(end)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // derived
  const totalUSD = useMemo(
    () => rows.reduce((acc, r) => acc + (Number(r.AmountPaidUSD) || 0), 0),
    [rows]
  );

  return (
    <AdminPageWrapper 
      title="Ad Provider Payments" 
      subtitle="View and manage all ad provider payment records"
    >

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2.2}>
            <TextField
              label="Start"
              type="date"
              value={toYMD(start)}
              onChange={handleStartChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.2}>
            <TextField
              label="End"
              type="date"
              value={toYMD(end)}
              onChange={handleEndChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.2}>
            <FormControl fullWidth>
              <InputLabel id="provider-filter">Provider</InputLabel>
              <Select
                labelId="provider-filter"
                label="Provider"
                value={providerId}
                onChange={(e) => setProviderId(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {providers.map((p) => (
                  <MenuItem key={p.ProviderId} value={String(p.ProviderId)}>
                    {p.ProviderName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2.2}>
            <FormControl fullWidth>
              <InputLabel id="app-filter">App</InputLabel>
              <Select
                labelId="app-filter"
                label="App"
                value={appId}
                onChange={(e) => setAppId(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {apps.map((a) => (
                  <MenuItem key={a.AppId} value={String(a.AppId)}>
                    {a.AppName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2.2}>
            <FormControl fullWidth>
              <InputLabel id="processed-filter">Processed</InputLabel>
              <Select
                labelId="processed-filter"
                label="Processed"
                value={onlyProcessed}
                onChange={(e) => setOnlyProcessed(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="1">Processed</MenuItem>
                <MenuItem value="0">Unprocessed</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={8} md={4}>
            <TextField
              label="Search (notes, app, provider)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyUp={(e) => {
                if (e.key === "Enter") fetchPayments({ resetPage: true });
              }}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={4} md="auto">
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Tooltip title="Refresh">
                <span>
                  <IconButton onClick={handleRefresh} disabled={loading}>
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<CalendarMonthIcon />}
                onClick={handleThisMonth}
              >
                This Month
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                color="secondary"
                onClick={exportCsv}
                disabled={!rows.length}
              >
                Export CSV
              </Button>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Period: {periodLabel} • Page Total USD: ${fmtMoney(totalUSD)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Professional Table */}
      <ProfessionalTable
        columns={[
          {
            header: 'Date',
            accessor: 'CreatedAt',
            render: (date) => (
              <Typography variant="body2">
                {date ? new Date(date).toLocaleString() : ""}
              </Typography>
            )
          },
          {
            header: 'Period',
            accessor: 'PeriodStart',
            render: (periodStart, row) => {
              const period = (periodStart ? toYMD(periodStart) : "") +
                (row.PeriodEnd ? ` → ${toYMD(row.PeriodEnd)}` : "");
              return (
                <Typography variant="body2">{period}</Typography>
              );
            }
          },
          {
            header: 'Provider',
            accessor: 'ProviderName',
            render: (providerName, row) => (
              <Typography variant="body2" fontWeight={500}>
                {providerName ?? row.ProviderId}
              </Typography>
            )
          },
          {
            header: 'App',
            accessor: 'AppName',
            render: (appName, row) => (
              <Typography variant="body2">
                {appName ?? row.AppId}
              </Typography>
            )
          },
          {
            header: 'Amount',
            accessor: 'AmountPaid',
            render: (amount) => (
              <Typography variant="body2" fontWeight={600} className="amount-positive">
                {fmtMoney(amount)}
              </Typography>
            ),
            style: { textAlign: 'right' }
          },
          {
            header: 'Cur',
            accessor: 'Currency',
            render: (currency) => (
              <Typography variant="body2" className="currency-code">
                {currency}
              </Typography>
            ),
            style: { textAlign: 'center' }
          },
          {
            header: 'FX→USD',
            accessor: 'FxRateToUSD',
            render: (rate) => (
              <Typography variant="body2">
                {fmtMoney(rate)}
              </Typography>
            ),
            style: { textAlign: 'right' }
          },
          {
            header: 'USD',
            accessor: 'AmountPaidUSD',
            render: (amount) => (
              <Typography variant="body2" fontWeight={600} className="amount-positive">
                {fmtMoney(amount)}
              </Typography>
            ),
            style: { textAlign: 'right' }
          },
          {
            header: 'Processed',
            accessor: 'IsProcessed',
            render: (isProcessed, row) => (
              <Switch
                checked={!!isProcessed}
                onChange={(e) => toggleProcessed(row, e.target.checked)}
                color="primary"
              />
            ),
            style: { textAlign: 'center' }
          },
          {
            header: 'Notes',
            accessor: 'Notes',
            render: (notes) => (
              <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {notes ?? "-"}
              </Typography>
            )
          }
        ]}
        data={rows.map((row, index) => ({ ...row, id: keyOf(row) || index }))}
        loading={loading}
        emptyMessage="No payments found"
      />

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={async (_e, newPage) => {
            setPage(newPage);
            await fetchPayments();
          }}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={async (e) => {
            const v = parseInt(e.target.value, 10);
            setRowsPerPage(v);
            setPage(0);
            await fetchPayments({ resetPage: true });
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />

      {/* Snackbar */}
      <Snackbar
        open={!!errorMsg || !!successMsg}
        autoHideDuration={3500}
        onClose={() => {
          setErrorMsg("");
          setSuccessMsg("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={errorMsg ? "error" : "success"}
          onClose={() => {
            setErrorMsg("");
            setSuccessMsg("");
          }}
          sx={{ width: "100%" }}
        >
          {errorMsg || successMsg}
        </Alert>
      </Snackbar>
    </AdminPageWrapper>
  );
}
